/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module NavigationModule
 */

import {
    ChangeDetectionStrategy,
    Component,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { L10nService } from '@vmw/ngx-vip';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import * as StateNames from 'state-names';
import * as SystemInfoSelectors from 'ng/root-store/system-info/system-info.selectors';
import * as globalL10n from 'global-l10n';
import { INavState } from '../../navigation.types';
import { NavigationService } from '../../services/navigation.service';
import * as l10n from './applications-vertical-nav.component.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Vertical Navigation component for the Applications pages.
 * @author alextsg
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'applications-vertical-nav',
    template: '<vertical-nav [states]="applicationStates$ | async"></vertical-nav>',
})
export class ApplicationsVerticalNavComponent {
    /**
     * List of navigation states and children that should be shown in the nav.
     */
    public applicationStates$: Observable<INavState[]> = of([]);

    constructor(
        navigationService: NavigationService,
        store: Store,
        l10nService: L10nService,
    ) {
        l10nService.registerSourceBundles(dictionary);

        const labelsHash = {
            [StateNames.APPLICATIONS_DASHBOARD]: {
                label: l10nService.getMessage(globalL10nKeys.dashboardLabel),
                icon: 'dashboard',
            },
            [StateNames.APPLICATIONS_VS]: {
                label: l10nService.getMessage(globalL10nKeys.virtualServicesLabel),
                icon: 'vmw-app',
            },
            [StateNames.APPLICATIONS_VS_VIP]: {
                label: l10nService.getMessage(l10nKeys.vsVipsLabel),
                icon: 'map-marker',
            },
            [StateNames.APPLICATIONS_POOL]: {
                label: l10nService.getMessage(l10nKeys.poolsLabel),
                icon: 'network-switch',
            },
            [StateNames.APPLICATIONS_POOL_GROUP]: {
                label: l10nService.getMessage(l10nKeys.poolGroupsLabel),
                icon: 'rack-server',
            },
            [StateNames.APPLICATIONS_GSLBSERVICE]: {
                label: l10nService.getMessage(l10nKeys.gslbServicesLabel),
                icon: 'network-globe',
            },
        };

        const states = navigationService.getChildren(StateNames.APPLICATIONS)
            .map(state => {
                const { label, icon } = labelsHash[state.name];
                const extendedState = {
                    ...state,
                    label,
                    icon,
                    children: state.children?.map(childState => {
                        const childStateProps = labelsHash[childState.name];

                        return {
                            ...childState,
                            label: childStateProps?.label,
                            icon: childStateProps?.icon,
                        };
                    }),
                };

                switch (state.name) {
                    case StateNames.APPLICATIONS_VS:
                    case StateNames.APPLICATIONS_POOL:
                    case StateNames.APPLICATIONS_GSLBSERVICE:
                        return {
                            ...extendedState,
                            children: undefined,
                        };
                    default:
                        return extendedState;
                }
            });

        this.applicationStates$ = store.select(SystemInfoSelectors.selectHasGslbConfig).pipe(
            map(hasGslb => {
                return states.filter(state => {
                    switch (state.name) {
                        case StateNames.APPLICATIONS_GSLBSERVICE:
                            return hasGslb;
                        default:
                            return true;
                    }
                });
            }),
        );
    }
}
